import { useEffect, useRef, useState } from 'react';
import type { MailRecipientsData } from '@/types/api';

/** What a send dialog hands back: the picked addresses, in send order. */
export type ChosenRecipients = {
  to: string[];
  cc: string[];
};

/**
 * Hold the To/Cc selection for a send, seeded from the server's suggestion once
 * per opening: a background refetch landing mid-edit must not wipe out what was
 * picked, and a stale selection from the last record must not carry over.
 */
export function useRecipientSelection(data: MailRecipientsData | undefined, active: boolean) {
  const [value, setValue] = useState<ChosenRecipients>({ to: [], cc: [] });
  const seeded = useRef(false);

  useEffect(() => {
    if (!active) {
      seeded.current = false;
      return;
    }
    if (!data || seeded.current) return;
    seeded.current = true;
    setValue({ to: data.to, cc: data.cc });
  }, [active, data]);

  return [value, setValue] as const;
}

/** "dana@acme.example" for one recipient, "3 addresses" beyond that. */
export function summarizeRecipients({ to, cc }: ChosenRecipients): string {
  const count = to.length + cc.length;
  if (count === 1) return to[0] ?? cc[0] ?? '';
  return `${count} addresses`;
}
